// Staged-op payload (v2) for a unified pull-plan draft. Mirrors the backend
// shape in services/pull_plan/draft_payload.py. Each array holds one category
// of change the pull-plan page accumulates before Publish.

import type { DepEditOp } from "../depEditor";

/** Whether a plan produces a Takt section (stamps Takt_SectionName, shows on
 *  the Takt board) or is a plain WBS-scoped plan (sequence-only, never takted).
 *  Chosen once at create; rides in the opaque payload. Absent ⇒ "takt". */
export type PullPlanKind = "takt" | "plain";

/** How a plan is being worked: edit the existing sequence in place ("adjust"),
 *  or wipe the not-started work and rebuild ("repull"). Rides in the opaque
 *  payload alongside `kind`. Absent ⇒ null (legacy drafts predate the fork). */
export type PullPlanMode = "adjust" | "repull";

export interface PendingTaktification {
  activity_id: number;
  takt_a?: string | null;
  takt_b?: string | null;
  takt_c?: string | null;
  takt_work: string;
}

export interface PendingWorkCodeAdd {
  code: string;
  prefix?: string;
  color?: string | null;
  label?: string | null;
  default_duration_days?: number | null;
}

/** A bare (A, B, C) zone tuple — the identity of one grid row's zone. */
export interface ZoneTuple {
  takt_a: string;
  takt_b: string;
  takt_c: string;
}

/** Renaming a zone row: relabel every activity in the `old` zone (active AND
 *  completed) to the `new` zone. On publish the backend re-zones them all and
 *  recomputes derived/sort fields — so no copy of the old zone survives. */
export interface PendingZoneRetag {
  old: ZoneTuple;
  new: ZoneTuple;
}

/** Deleting a zone row: strip its activities' Takt placement (A/B/C, Code,
 *  GrpName, Suffix, WBS_LU, Sort) while keeping their work code + section, so
 *  they drop off the board and return to the section's untagged palette. */
export type PendingUntaktification = ZoneTuple;

/** A zone (grid row) in its display order. On publish the backend rewrites
 *  Takt_SortB so the section's zones sort in this sequence. */
export type PendingZoneOrder = ZoneTuple;

export interface PendingNewActivity {
  /** FE-generated id, survives the JSON round-trip; remapped to a real
   *  activity id at publish time. */
  draft_id: string;
  name: string;
  /** The WBS leaf this activity is placed in on the board — where it gets
   *  CREATED at publish. Synced from the proto's wbsNodeId at save (it tracks
   *  the activity's current lane, including moves). Null/absent falls back to
   *  the section's WBS node on the backend. */
  wbs_node_id?: number | null;
  takt_a?: string | null;
  takt_b?: string | null;
  takt_c?: string | null;
  work_code?: string | null;
  duration_days?: number | null;
}

export interface PendingActivityDelete {
  activity_id: number;
}

/** A duration change to an existing (committed) activity. New-activity
 *  durations ride on pending_new_activities instead. */
export interface PendingDurationEdit {
  activity_id: number;
  duration_days: number;
}

/** A task_name change to an existing (committed) activity. New-activity
 *  names ride on pending_new_activities instead. */
export interface PendingRenameEdit {
  activity_id: number;
  name: string;
}

/** Full-untag an activity: strip its work code + zone (keeping SectionName +
 *  SortA) so it returns to the section's untagged palette. The activity is NOT
 *  deleted. Drives "remove from work code" and, per carrier, "delete work code". */
export interface PendingWorkCodeRemoval {
  activity_id: number;
}

/** Rename/recolor/re-code a work code for THIS section's carriers only (a
 *  section-local fork). `code` is the committed lookup key (never changes once
 *  staged); `new_code` re-codes the section's carriers' Takt_Work value itself
 *  (e.g. "201" → "M-201"). A name change re-tags those carriers to (code, new
 *  prefix); the code's other-section carriers keep their name/color/value. */
export interface PendingWorkCodeEdit {
  code: string;
  new_code?: string;
  prefix?: string;
  label?: string | null;
  color?: string | null;
}

/** Hide a deleted code's chip from THIS section's legend (display-only — the
 *  actual untag rides on pending_work_code_removals). */
export interface PendingWorkCodeDelete {
  code: string;
  prefix?: string;
}

/** Claim a FOREIGN scope member (tagged to another Takt section) into this
 *  plan. Publish stamps Takt_SectionName = this section and the member joins
 *  the planned replan set; no code/zone required. */
export interface PendingSectionClaim {
  activity_id: number;
}

/** The full v2 draft payload. Every array defaults to empty. */
export interface DraftPayload {
  /** Takt section vs plain (non-Takt) plan. Absent ⇒ "takt" (the legacy
   *  default), so existing drafts read unchanged. Opaque to the backend. */
  kind?: PullPlanKind;
  /** The plan's scope: explicit WBS-leaf ids, written into the payload at
   *  create (backend-opaque thereafter). Carried through every save so the
   *  scope is never dropped from the payload. */
  scope_wbs_ids: number[];
  /** Scope members deliberately LEFT OUT of the plan ("kept") — backend-opaque
   *  decision state, summaries only, persisted like anchor_reviews. */
  member_keeps: number[];
  pending_section_claims: PendingSectionClaim[];
  pending_dep_edits: DepEditOp[];
  pending_taktifications: PendingTaktification[];
  pending_work_code_adds: PendingWorkCodeAdd[];
  pending_zone_retags: PendingZoneRetag[];
  pending_untaktifications: PendingUntaktification[];
  pending_zone_order: PendingZoneOrder[];
  pending_new_activities: PendingNewActivity[];
  pending_activity_deletes: PendingActivityDelete[];
  pending_duration_edits: PendingDurationEdit[];
  pending_rename_edits: PendingRenameEdit[];
  pending_work_code_removals: PendingWorkCodeRemoval[];
  pending_work_code_edits: PendingWorkCodeEdit[];
  pending_work_code_deletes: PendingWorkCodeDelete[];
}

export function emptyDraftPayload(): DraftPayload {
  return {
    scope_wbs_ids: [],
    member_keeps: [],
    pending_section_claims: [],
    pending_dep_edits: [],
    pending_taktifications: [],
    pending_work_code_adds: [],
    pending_zone_retags: [],
    pending_untaktifications: [],
    pending_zone_order: [],
    pending_new_activities: [],
    pending_activity_deletes: [],
    pending_duration_edits: [],
    pending_rename_edits: [],
    pending_work_code_removals: [],
    pending_work_code_edits: [],
    pending_work_code_deletes: [],
  };
}

/** Parse an opaque payload_json blob into a normalized v2 DraftPayload.
 *  A blank/malformed blob yields an empty payload. */
export function parseDraftPayload(payloadJson: string | null | undefined): DraftPayload {
  const base = emptyDraftPayload();
  if (!payloadJson) return base;
  let data: unknown;
  try {
    data = JSON.parse(payloadJson);
  } catch {
    return base;
  }
  if (typeof data !== "object" || data === null) return base;
  const obj = data as Record<string, unknown>;
  const numberArray = (v: unknown): number[] =>
    Array.isArray(v) ? v.filter((n): n is number => typeof n === "number") : [];
  return {
    kind: obj.kind === "plain" || obj.kind === "takt" ? obj.kind : undefined,
    scope_wbs_ids: numberArray(obj.scope_wbs_ids),
    member_keeps: numberArray(obj.member_keeps),
    pending_section_claims: Array.isArray(obj.pending_section_claims)
      ? obj.pending_section_claims
      : [],
    pending_dep_edits: Array.isArray(obj.pending_dep_edits) ? obj.pending_dep_edits : [],
    pending_taktifications: Array.isArray(obj.pending_taktifications) ? obj.pending_taktifications : [],
    pending_work_code_adds: Array.isArray(obj.pending_work_code_adds) ? obj.pending_work_code_adds : [],
    pending_zone_retags: Array.isArray(obj.pending_zone_retags) ? obj.pending_zone_retags : [],
    pending_untaktifications: Array.isArray(obj.pending_untaktifications) ? obj.pending_untaktifications : [],
    pending_zone_order: Array.isArray(obj.pending_zone_order) ? obj.pending_zone_order : [],
    pending_new_activities: Array.isArray(obj.pending_new_activities) ? obj.pending_new_activities : [],
    pending_activity_deletes: Array.isArray(obj.pending_activity_deletes) ? obj.pending_activity_deletes : [],
    pending_duration_edits: Array.isArray(obj.pending_duration_edits) ? obj.pending_duration_edits : [],
    pending_rename_edits: Array.isArray(obj.pending_rename_edits) ? obj.pending_rename_edits : [],
    pending_work_code_removals: Array.isArray(obj.pending_work_code_removals) ? obj.pending_work_code_removals : [],
    pending_work_code_edits: Array.isArray(obj.pending_work_code_edits) ? obj.pending_work_code_edits : [],
    pending_work_code_deletes: Array.isArray(obj.pending_work_code_deletes) ? obj.pending_work_code_deletes : [],
  };
}

/** Read the adjust/repull fork decision (and kind) straight from an opaque
 *  payload blob — the single source of truth for routing a draft to the
 *  re-pull entry gate vs. the board. `kind` defaults "takt"; `mode` is null
 *  when absent or malformed (legacy drafts predate the fork). Kept separate
 *  from the strict DraftPayload interface so `mode` stays opaque to the backend
 *  and to the staged-op layer. */
export function readPayloadMode(
  payloadJson: string | null | undefined,
): { mode: PullPlanMode | null; kind: PullPlanKind } {
  let data: unknown;
  if (payloadJson) {
    try {
      data = JSON.parse(payloadJson);
    } catch {
      data = null;
    }
  }
  const obj =
    typeof data === "object" && data !== null
      ? (data as Record<string, unknown>)
      : {};
  const mode: PullPlanMode | null =
    obj.mode === "adjust" || obj.mode === "repull" ? obj.mode : null;
  const kind: PullPlanKind = obj.kind === "plain" ? "plain" : "takt";
  return { mode, kind };
}
